import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DownloadOutlined, UploadOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Button,
  Card,
  Collapse,
  DatePicker,
  InputNumber,
  Popconfirm,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
  Upload,
  message,
} from 'antd';
import dayjs from 'dayjs';
import { API } from '@/services/api.service';
import HowToCard from '../HowToCard';
import { useGoToSection } from '../howToLinks';
import { errorText, formatAmount, saveBlob } from '../format';
import ParentPlImportModal from './ParentPlImportModal';
import ParentPlSettings from './ParentPlSettings';

const { Text } = Typography;

const pairKey = (u) => `${u.account}|${u.orgUnit}`;
const period = (m, y) => `${String(m).padStart(2, '0')}.${y}`;

/**
 * The parent's income statement of one company (e.g. Berkosan): months already
 * sent stay as they were sent, the following ones come from the turnover of
 * classes 5 and 6, each account and organisational unit in the line it is
 * mapped to. Lines the parent fills from outside the books (raw material in kg)
 * are typed in for the month.
 *
 * @param {Object} props - Component props
 * @param {string} props.companyId - Company whose ledger is used
 * @returns {JSX.Element} The panel
 */
export default function ParentPlPanel({ companyId }) {
  const { t } = useTranslation('clientReports');
  const queryClient = useQueryClient();
  // The report goes out after the month closes, so last month is the default.
  const [month0, setMonth0] = useState(dayjs().subtract(1, 'month').startOf('month'));
  const [importFile, setImportFile] = useState(null);
  const [picked, setPicked] = useState({});
  const [typed, setTyped] = useState({});
  const year = month0.year();
  const month = month0.month() + 1;

  const { data, isLoading, error } = useQuery({
    queryKey: ['client-reports-parent-pl', companyId, year, month],
    queryFn: () => API.clientReports.getParentIncomeStatement(companyId, { year, month }),
    retry: false,
  });
  const report = data?.data;
  const [openSections, setOpenSections] = useState([]);
  useGoToSection('parentPl', (key) => setOpenSections((keys) => [...new Set([...keys, key])]));

  const current = report?.months?.find((mo) => mo.month === month);
  const manualLines = useMemo(() => (report?.lines || []).filter((l) => l.kind === 'manual'), [report]);

  useEffect(() => {
    if (!current) return;
    setTyped(Object.fromEntries(manualLines.map((l) => [l.key, current.values[l.key] ?? null])));
  }, [current, manualLines]);

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['client-reports-parent-pl', companyId] });
    queryClient.invalidateQueries({ queryKey: ['client-reports-parent-pl-config', companyId] });
  };

  const exportMutation = useMutation({
    mutationFn: () => API.clientReports.exportParentIncomeStatement(companyId, { year, month }),
    onSuccess: saveBlob,
    onError: async (e) => message.error(await errorText(e)),
  });

  const unfreezeMutation = useMutation({
    mutationFn: (m) => API.clientReports.unfreezeParentIncomeStatementMonth(companyId, { year, month: m }),
    onSuccess: () => {
      message.success(t('parentPl.unfrozen'));
      refresh();
    },
    onError: async (e) => message.error(await errorText(e)),
  });

  // A pair the ledger has no line for, mapped straight from the warning.
  const mapMutation = useMutation({
    mutationFn: ({ account, orgUnit, line }) =>
      API.clientReports.saveParentIncomeStatementConfig(companyId, {
        mapping: [...(report?.rules?.mapping || []), { account, orgUnit, line }],
      }),
    onSuccess: (_, u) => {
      message.success(t('parentPl.mapped', { account: u.account, orgUnit: u.orgUnit || '—' }));
      setPicked((p) => ({ ...p, [pairKey(u)]: undefined }));
      refresh();
    },
    onError: async (e) => message.error(await errorText(e)),
  });

  const manualMutation = useMutation({
    mutationFn: () =>
      API.clientReports.saveParentIncomeStatementConfig(companyId, {
        manual: { year, month, values: typed },
      }),
    onSuccess: () => {
      message.success(t('parentPl.manualSaved'));
      refresh();
    },
    onError: async (e) => message.error(await errorText(e)),
  });

  const ledgerLines = useMemo(() => (report?.lines || []).filter((l) => l.kind === 'ledger'), [report]);
  const lineOptions = useMemo(
    () => ledgerLines.map((l) => ({ value: l.key, label: l.label })),
    [ledgerLines]
  );
  const revenue = useMemo(() => new Set(report?.revenueLines || []), [report]);

  // The form's result: revenue lines less every other ledger line.
  const formResult = (values) =>
    ledgerLines.reduce((s, l) => s + (revenue.has(l.key) ? 1 : -1) * (values[l.key] || 0), 0);

  const unmapped = useMemo(() => {
    const byPair = {};
    (report?.months || []).forEach((mo) =>
      mo.unmapped.forEach((u) => {
        const key = pairKey(u);
        if (!byPair[key]) byPair[key] = { ...u, key, months: [] };
        byPair[key].months.push({ month: mo.month, amount: u.debit - u.credit });
      })
    );
    return Object.values(byPair);
  }, [report]);

  const rows = useMemo(() => {
    if (!report) return [];
    return [
      ...report.lines.map((l) => ({ key: l.key, label: l.label, kind: l.kind })),
      { key: '__form', label: t('parentPl.formResult'), summary: true },
      { key: '__ledger', label: t('parentPl.ledgerResult'), summary: true },
    ];
  }, [report, t]);

  const cellFor = (row, mo) => {
    if (row.key === '__form') return <Text strong>{formatAmount(formResult(mo.values))}</Text>;
    if (row.key === '__ledger') {
      const off = !mo.frozen && Math.abs(formResult(mo.values) - mo.ledgerResult) >= 0.01;
      return (
        <Text strong type={off ? 'danger' : undefined}>
          {formatAmount(mo.ledgerResult)}
        </Text>
      );
    }
    const value = mo.values[row.key];
    if (row.kind === 'manual') return value == null ? '' : formatAmount(value);
    const ledger = mo.computed[row.key] || 0;
    const differs = mo.frozen && Math.abs((value || 0) - ledger) >= 1;
    const text = Math.abs(value || 0) >= 0.005 ? formatAmount(value) : '';
    if (!differs) return text;
    return (
      <Tooltip title={t('parentPl.ledgerValue', { amount: formatAmount(ledger) })}>
        <span style={{ borderBottom: '1px dashed #d48806' }}>{text || '0,00'}</span>
      </Tooltip>
    );
  };

  const columns = useMemo(() => {
    if (!report) return [];
    return [
      {
        title: t('parentPl.line'),
        dataIndex: 'label',
        key: 'label',
        fixed: 'left',
        width: 320,
        render: (label, row) => {
          if (row.summary) return <Text strong>{label}</Text>;
          if (row.kind === 'manual') {
            return (
              <Space size={6}>
                {label}
                <Tag color="gold" style={{ marginInlineEnd: 0 }}>{t('parentPl.manualTag')}</Tag>
              </Space>
            );
          }
          return label;
        },
      },
      ...report.months.map((mo) => ({
        key: `m${mo.month}`,
        align: 'right',
        width: 140,
        title: (
          <Space direction="vertical" size={2} style={{ alignItems: 'flex-end' }}>
            <span>{period(mo.month, year)}</span>
            {mo.frozen ? (
              <Space size={4}>
                <Tooltip
                  title={t('parentPl.frozenAt', {
                    date: dayjs(mo.frozen.frozenAt).format('DD.MM.YYYY HH:mm'),
                  })}
                >
                  <Tag color={mo.frozen.source === 'upload' ? 'purple' : 'blue'} style={{ marginInlineEnd: 0 }}>
                    {t(`parentPl.frozen.${mo.frozen.source}`)}
                  </Tag>
                </Tooltip>
                <Popconfirm
                  title={t('parentPl.unfreezeConfirm')}
                  okText={t('parentPl.unfreeze')}
                  cancelText={t('fccs.cancel')}
                  onConfirm={() => unfreezeMutation.mutate(mo.month)}
                >
                  <Button type="link" size="small" style={{ padding: 0, height: 'auto' }}>
                    {t('parentPl.unfreeze')}
                  </Button>
                </Popconfirm>
              </Space>
            ) : (
              <Tag style={{ marginInlineEnd: 0 }}>{t('parentPl.fromLedger')}</Tag>
            )}
          </Space>
        ),
        render: (_, row) => cellFor(row, mo),
      })),
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [report, t, year]);

  // 404 = nothing has arrived from AB for that year yet.
  const errorMessage =
    error?.response?.status === 404
      ? t('fccs.noLedger', { year })
      : error?.response?.data?.message || error?.message;

  const manualDirty =
    current &&
    manualLines.some((l) => (typed[l.key] ?? null) !== (current.values[l.key] ?? null));

  return (
    <div>
      <HowToCard guide="parentPl" open={report ? !report.template || !report.configured : undefined} />
      <Space id="howto-parentPl-toolbar" wrap style={{ display: 'flex', marginBottom: 12 }}>
        <DatePicker
          picker="month"
          allowClear={false}
          value={month0}
          format="MM.YYYY"
          onChange={(value) => value && setMonth0(value.startOf('month'))}
        />
        <Button
          type="primary"
          shape="round"
          className="btn-primary"
          icon={<DownloadOutlined />}
          disabled={!report?.template}
          loading={exportMutation.isPending}
          onClick={() => exportMutation.mutate()}
        >
          {t('parentPl.download')}
        </Button>
        <Upload
          accept=".xlsx"
          showUploadList={false}
          beforeUpload={(file) => {
            setImportFile(file);
            return false;
          }}
        >
          <Button shape="round" icon={<UploadOutlined />}>
            {t('parentPl.importButton')}
          </Button>
        </Upload>
      </Space>

      <div style={{ fontSize: 12, opacity: 0.7, marginBottom: 8 }}>{t('parentPl.hint')}</div>

      {errorMessage && (
        <Alert type="warning" showIcon style={{ marginBottom: 12 }} message={errorMessage} />
      )}
      {report && !report.template && (
        <Alert type="info" showIcon style={{ marginBottom: 12 }} message={t('parentPl.noTemplate')} />
      )}
      {report?.template && (
        <Text type="secondary" style={{ display: 'block', marginBottom: 6 }}>
          {t('parentPl.templateInfo', {
            file: report.template.fileName || report.template.sheetName,
            date: dayjs(report.template.uploadedAt).format('DD.MM.YYYY'),
            pairs: report.rules.mapping.length,
          })}
        </Text>
      )}

      {report?.template && unmapped.length > 0 && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message={t('parentPl.unmappedTitle')}
          description={
            <Space direction="vertical" style={{ width: '100%' }}>
              {unmapped.map((u) => (
                <Space key={u.key} wrap>
                  <Text strong>{u.account}</Text>
                  {u.orgUnit ? <Tag style={{ marginInlineEnd: 0 }}>{u.orgUnit}</Tag> : <Tag color="red">{t('parentPl.noOrgUnit')}</Tag>}
                  {report.accountNames?.[u.account] && (
                    <Text type="secondary">{report.accountNames[u.account]}</Text>
                  )}
                  <Text>
                    {u.months.map((mo) => `${period(mo.month, year)}: ${formatAmount(mo.amount)}`).join(' · ')}
                  </Text>
                  <Select
                    showSearch
                    optionFilterProp="label"
                    style={{ width: 340 }}
                    placeholder={t('parentPl.pickLine')}
                    options={lineOptions}
                    value={picked[u.key]}
                    onChange={(line) => setPicked((p) => ({ ...p, [u.key]: line }))}
                  />
                  <Button
                    shape="round"
                    disabled={!picked[u.key]}
                    loading={mapMutation.isPending && pairKey(mapMutation.variables || {}) === u.key}
                    onClick={() => mapMutation.mutate({ account: u.account, orgUnit: u.orgUnit, line: picked[u.key] })}
                  >
                    {t('parentPl.mapPair')}
                  </Button>
                </Space>
              ))}
            </Space>
          }
        />
      )}

      {report?.template && current && manualLines.length > 0 && (
        <Card id="howto-parentPl-manual" size="small" style={{ marginBottom: 12 }} title={t('parentPl.manualTitle', { period: period(month, year) })}>
          {current.frozen ? (
            <Text type="secondary">{t('parentPl.manualFrozen')}</Text>
          ) : (
            <Space wrap>
              {manualLines.map((l) => (
                <Space key={l.key} size={6}>
                  <Text>{l.label}</Text>
                  <InputNumber
                    style={{ width: 160 }}
                    decimalSeparator=","
                    step={0.01}
                    value={typed[l.key]}
                    onChange={(value) => setTyped((v) => ({ ...v, [l.key]: value }))}
                  />
                </Space>
              ))}
              <Button
                type="primary"
                shape="round"
                className="btn-primary"
                disabled={!manualDirty}
                loading={manualMutation.isPending}
                onClick={() => manualMutation.mutate()}
              >
                {t('parentPl.manualSave')}
              </Button>
              {manualLines.some((l) => current.values[l.key] == null) && (
                <Text type="warning">{t('parentPl.manualMissing')}</Text>
              )}
            </Space>
          )}
        </Card>
      )}

      {report?.template && (
        <Table
          scroll={{ x: 'max-content' }}
          rowKey="key"
          size="small"
          pagination={false}
          loading={isLoading}
          columns={columns}
          dataSource={rows}
        />
      )}

      <Collapse
        style={{ marginTop: 16 }}
        activeKey={openSections}
        onChange={setOpenSections}
        items={[
          {
            key: 'settings',
            label: t('parentPl.settingsTitle'),
            children: (
              <div id="howto-parentPl-settings">
                <ParentPlSettings companyId={companyId} onSaved={refresh} />
              </div>
            ),
          },
        ]}
      />

      {importFile && (
        <ParentPlImportModal
          companyId={companyId}
          file={importFile}
          year={year}
          onClose={() => setImportFile(null)}
          onApplied={() => {
            setImportFile(null);
            refresh();
          }}
        />
      )}
    </div>
  );
}
